import { useRef, useState } from 'react';
import { transcribeSpeech } from '@/lib/api/ai';

/**
 * Push-to-talk that actually works.
 *
 * <p>
 * Replaces the browser's Web Speech API. That was on-device and free, but it mishears dental
 * vocabulary badly and does not exist in Firefox at all — unusable in practice, which is what
 * prompted this. Audio now goes to Whisper server-side, the same engine behind consultation
 * dictation, which is the one people already find reliable.
 * </p>
 *
 * <p>
 * Records with <c>MediaRecorder</c> and uploads on stop, so there is a real "recording" state to
 * show. The caller decides what to do with the text: the assistant sends it, a composer would put
 * it in the box to edit first.
 * </p>
 */
export const useVoiceInput = ({ onResult, onError }: {
  onResult: (transcript: string) => void;
  onError?: (message: string) => void;
}) => {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Feature-detect rather than assume: without a microphone or MediaRecorder the caller should hide
  // the button entirely instead of offering something that cannot work.
  const supported = typeof window !== 'undefined'
    && typeof MediaRecorder !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia;

  const stop = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const start = async () => {
    if (!supported || busy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };

      recorder.onstop = async () => {
        // Release the microphone before the upload, not after — the browser's recording indicator
        // should stop the moment the user stops speaking, not when the network does.
        stream.getTracks().forEach((track) => track.stop());

        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size === 0) return;

        setBusy(true);
        try {
          const { text } = await transcribeSpeech(blob);
          if (text) onResult(text);
        } catch {
          onError?.('transcribe-failed');
        } finally {
          setBusy(false);
        }
      };

      recorder.start();
      setRecording(true);
    } catch {
      // Denied permission or no device. Silent — the browser has already told them.
      setRecording(false);
    }
  };

  return { supported, recording, busy, start, stop, toggle: () => (recording ? stop() : void start()) };
};
